import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import Reveal from "@/components/Reveal";
import {
  TShirt,
  SneakerMove,
  Buildings,
  Bank,
  ShoppingBagOpen,
  Factory,
} from "@phosphor-icons/react";

const SECTORS = [
  { Icon: TShirt, name: "Moda & Calzature", years: "8 anni", note: "Brand luxury e mid-market" },
  { Icon: SneakerMove, name: "Sport & Abbigliamento", years: "5 anni", note: "Retail e wholesale" },
  { Icon: Buildings, name: "Immobiliare", years: "14 anni", note: "Residenziale e commerciale" },
  { Icon: Bank, name: "Servizi per la PA", years: "6 anni", note: "Gare pubbliche e appalti" },
  { Icon: ShoppingBagOpen, name: "Retail", years: "10 anni", note: "Multi-store e franchising" },
  { Icon: Factory, name: "Industria & Manifattura", years: "15 anni", note: "Produzione e supply chain" },
];

const MARQUEE_WORDS = [
  "Strategia",
  "Controllo di Gestione",
  "M&A",
  "Ristrutturazioni",
  "Governance",
  "Finanza Alternativa",
  "Crescita",
  "Nord Est",
];

export default function Settori() {
  return (
    <section
      id="settori"
      data-testid="section-settori"
      className="relative py-32 md:py-40 lg:py-48 overflow-hidden bg-navy-deep"
    >
      <div
        aria-hidden
        className="absolute top-16 right-6 md:right-10 font-display italic text-[14rem] md:text-[22rem] leading-none text-gold/[0.05] select-none pointer-events-none"
      >
        06
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="max-w-3xl mb-16 md:mb-24">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-gold" />
              <span
                className="text-[0.7rem] uppercase text-gold font-sans-crisp font-semibold"
                style={{ letterSpacing: "0.3em" }}
              >
                Capitolo 06 · Esperienza
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              data-testid="settori-title"
              className="font-display font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-ink"
            >
              Settori di <span className="italic-serif text-gold">esperienza</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-ink/70 leading-relaxed font-sans-crisp max-w-2xl">
              Oltre vent'anni di lavoro sul campo in industrie molto diverse tra loro,
              accomunate da una stessa esigenza: crescere con metodo.
            </p>
          </Reveal>
        </div>

        {/* Grid of sectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gold/15">
          {SECTORS.map((s, i) => (
            <motion.div
              key={s.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
              className="group relative bg-navy-deep hover:bg-navy-mid/50 transition-colors duration-700 p-8 md:p-10 lg:p-12 min-h-[280px] flex flex-col justify-between"
              data-testid={`settore-${i + 1}`}
            >
              <div className="flex items-start justify-between">
                <span
                  className="font-display italic text-gold text-sm"
                  style={{ letterSpacing: "0.1em" }}
                >
                  0{i + 1}
                </span>
                <s.Icon size={44} weight="light" className="text-gold group-hover:scale-110 transition-transform duration-700 origin-right" />
              </div>

              <div className="mt-16">
                <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-2 group-hover:text-gold transition-colors duration-500">
                  {s.name}
                </h3>
                <p className="text-ink/60 text-sm font-sans-crisp mb-6">{s.note}</p>
                <div className="hairline mb-4" />
                <p
                  className="text-[0.7rem] uppercase text-gold/85 font-sans-crisp font-medium"
                  style={{ letterSpacing: "0.24em" }}
                >
                  Esperienza · {s.years}
                </p>
              </div>

              {/* thin diagonal reveal */}
              <span className="absolute inset-0 border border-transparent group-hover:border-gold/50 transition-colors duration-700 pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* Editorial marquee */}
        <div className="mt-24 md:mt-32 border-y border-gold/15 py-8 md:py-10">
          <Marquee gradient={false} speed={35} pauseOnHover>
            {MARQUEE_WORDS.map((w, i) => (
              <span key={i} className="flex items-center gap-8 mr-8" aria-hidden>
                <span
                  className="font-display italic text-4xl md:text-6xl text-ink/85"
                  style={{ letterSpacing: "0.01em" }}
                >
                  {w}
                </span>
                <span className="w-2 h-2 rounded-full bg-gold" />
              </span>
            ))}
          </Marquee>
        </div>
      </div>
    </section>
  );
}
